import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '@framework/ui/atoms/Loading';
import type { GroupMemberInvitation, GroupMemberSignUpMethod } from '@group/domain';
import { LoginMethodMenu, LoginWithEmailFormContainer } from '@user/Login';
import { useFirebaseAuthRedirectWithGoogle } from '@user/Login/useFirebaseAuthRedirectWithGoogle';
import { toast } from 'react-hot-toast';
import { SignUpMethodMenu } from './SignUpMethodMenu';

type Props = {
  invitation: GroupMemberInvitation;
  availableSignUpMethods: GroupMemberSignUpMethod;
};

/**
 * 未ログイン状態のユーザ向けの、グループメンバーへの招待確認メニュー
 * @returns
 */
export const UnauthenticatedMenu: React.FC<Props> = ({ availableSignUpMethods, invitation }: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const firebaseAuthRedirectWithGoogle = useFirebaseAuthRedirectWithGoogle();

  const handleLoginWithGoogle = useCallback(async () => {
    setProcessing(true);
    try {
      // 成功時はページが redirect で離脱するため processing は戻さない。
      await firebaseAuthRedirectWithGoogle({ withInvitation: true });
    } catch (e) {
      console.error(e);
      toast.error(t('auth.login.processingError'));
      setProcessing(false);
    }
  }, [firebaseAuthRedirectWithGoogle, t]);
  const [showLoginWithEmailForm, setShowLoginWithEmailForm] = useState<boolean>(false);
  const handleLoginWithEmail = useCallback(() => setShowLoginWithEmailForm(true), []);
  const handleCloseLoginWithPassword = useCallback(() => setShowLoginWithEmailForm(false), []);

  const [showSignUpMenu, setShowSignUpMenu] = useState(true);
  const handleShowSignUpMenu = useCallback(() => setShowSignUpMenu(false), []);
  const handleCloseSignUpMenu = useCallback(() => setShowSignUpMenu(true), []);

  if (processing) {
    return (
      <div className="flex w-80 flex-col space-y-4 px-5 py-2">
        <Loading message={t('auth.login.processing')} />
      </div>
    );
  }

  if (showLoginWithEmailForm) {
    return <LoginWithEmailFormContainer onClose={handleCloseLoginWithPassword} />;
  }

  if (showSignUpMenu) {
    return (
      <SignUpMethodMenu
        availableSignUpMethods={availableSignUpMethods}
        invitation={invitation}
        onLogin={handleShowSignUpMenu}
      />
    );
  }

  return (
    <>
      <div className="flex w-80 flex-col space-y-4 px-5 py-2">
        <div className="cursor-pointer font-bold text-brand hover:underline" onClick={handleCloseSignUpMenu}>
          {t('auth.back.label')}
        </div>
      </div>
      <LoginMethodMenu onLoginWithGoogle={handleLoginWithGoogle} onLoginWithEmail={handleLoginWithEmail} />
    </>
  );
};
